import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { ChevronBack, ChevronRight } from '@withjoy/joykit/icons';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';
import { STYLE_FACET } from '../Filters/Filters.constants';
import { useTranslation } from '@shared/core';
import { Section, GalleryHeading, ScrollWrapper, ScrollContainer, ArrowButton, StyleItem, ThumbnailWrapper, Thumbnail, Label } from './StyleShowcase.styles';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

type StyleDefinition = Readonly<{
  /** Tag name to toggle in the filter system. */
  tagName: string;
  /** Display label */
  label: string;
  /** Asset filename per category (without extension). Falls back to `assetKey` when no override. */
  assetKey: string;
  assetKeyOverrides?: Partial<Record<StationeryTemplateCategoryEnum, string>>;
}>;

const STYLE_DEFINITIONS: StyleDefinition[] = [
  { tagName: 'botanical', label: 'Botanical', assetKey: 'botanical' },
  {
    tagName: 'floral',
    label: 'Floral',
    assetKey: 'floral',
    assetKeyOverrides: { [StationeryTemplateCategoryEnum.invitation]: 'floral-v2' },
  },
  { tagName: 'elegant', label: 'Elegant', assetKey: 'elegant_classic' },
  { tagName: 'destination', label: 'Destination', assetKey: 'destination-v2' },
  { tagName: 'typographic', label: 'Typographic', assetKey: 'typographic' },
  {
    tagName: 'rustic',
    label: 'Rustic',
    assetKey: 'rustic-v2',
    assetKeyOverrides: { [StationeryTemplateCategoryEnum.invitation]: 'rustic-v3' },
  },
  { tagName: 'minimal', label: 'Minimal', assetKey: 'minimal' },
  { tagName: 'vintage', label: 'Vintage', assetKey: 'vintage' },
  { tagName: 'whimsical', label: 'Whimsical', assetKey: 'whimsical' },
];

const CATEGORY_TO_ASSET_PATH: Partial<Record<StationeryTemplateCategoryEnum, string>> = {
  [StationeryTemplateCategoryEnum.saveTheDate]: 'save-the-date-gallery',
  [StationeryTemplateCategoryEnum.invitation]: 'invites-gallery',
};

const SUPPORTED_CATEGORIES = new Set([StationeryTemplateCategoryEnum.saveTheDate, StationeryTemplateCategoryEnum.invitation]);

const S3_BASE = 'https://withjoy.com/assets/public/marcom-prod';

function getAssetUrl(category: StationeryTemplateCategoryEnum, style: StyleDefinition): string {
  const galleryPath = CATEGORY_TO_ASSET_PATH[category];
  const key = style.assetKeyOverrides?.[category] ?? style.assetKey;
  return `${S3_BASE}/${galleryPath}/styles/${key}.png`;
}

/**
 * Build the composite code for a style definition.
 * e.g. tagName: 'elegant' → 'style:elegant'
 */
function toCompositeCode(style: StyleDefinition): string {
  return `${STYLE_FACET}:${style.tagName}`;
}

function isStyleActive(activeFilters: ReadonlySet<string>, style: StyleDefinition): boolean {
  return activeFilters.has(toCompositeCode(style));
}

const SCROLL_AMOUNT = 240;

type StyleShowcaseProps = Readonly<{
  category: StationeryTemplateCategoryEnum;
}>;

export const StyleShowcase: React.FC<StyleShowcaseProps> = ({ category }) => {
  const { activePrintFilters: activeFilters, setActiveFilterGroup } = useDesignGalleryFiltersContext();
  const { buttonInteracted } = useCardTelemetry();
  const { tq } = useTranslation('stationery');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrowVisibility = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftArrow(scrollLeft > 2);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrowVisibility();

    const observer = createSafeResizeObserver(updateArrowVisibility, { throttle: false });
    observer.observe(el);
    el.addEventListener('scroll', updateArrowVisibility, { passive: true });

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateArrowVisibility);
    };
  }, [updateArrowVisibility]);

  if (!SUPPORTED_CATEGORIES.has(category)) {
    return null;
  }

  const heading =
    category === StationeryTemplateCategoryEnum.saveTheDate ? tq`Save the Date Cards` : category === StationeryTemplateCategoryEnum.invitation ? tq`Wedding Invitations` : null;

  /** SEO-friendly alt text: e.g. "rustic save the date cards" or "floral wedding invitations" */
  const getStyleAlt = (styleLabel: string) => {
    const label = styleLabel.toLowerCase();
    if (category === StationeryTemplateCategoryEnum.saveTheDate) return tq`${label} save the date cards`;
    if (category === StationeryTemplateCategoryEnum.invitation) return tq`${label} wedding invitations`;
    return tq`${label} cards`;
  };

  const handleScroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const handleStyleClick = (style: StyleDefinition) => {
    const isActive = isStyleActive(activeFilters, style);
    const code = toCompositeCode(style);

    buttonInteracted({
      name: 'designsGallery_styleShowcaseClicked',
      facet: 'style',
      code,
      isChecked: !isActive,
    });

    // Single-select: the clicked style replaces the whole style group, and
    // clicking the active style clears it. One call so it is one history entry.
    setActiveFilterGroup(STYLE_FACET, isActive ? [] : [style.tagName]);
  };

  return (
    <Section>
      {heading && <GalleryHeading>{heading}</GalleryHeading>}
      {showLeftArrow && (
        <ArrowButton
          $dir="left"
          onClick={() => handleScroll('left')}
          aria-label={tq`Scroll styles left`}
        >
          <ChevronBack
            aria-hidden
            title={undefined}
            size={20}
          />
        </ArrowButton>
      )}
      <ScrollWrapper>
        <ScrollContainer
          ref={scrollRef}
          role="group"
          aria-label={tq`Filter by style`}
        >
          {STYLE_DEFINITIONS.map(style => {
            const isActive = isStyleActive(activeFilters, style);
            return (
              <StyleItem
                key={style.assetKey}
                onClick={() => handleStyleClick(style)}
                aria-pressed={isActive}
                aria-label={tq`Filter by ${style.label} style`}
              >
                <ThumbnailWrapper $isActive={isActive}>
                  <Thumbnail
                    src={getAssetUrl(category, style)}
                    alt={getStyleAlt(style.label)}
                    loading="lazy"
                  />
                </ThumbnailWrapper>
                <Label $isActive={isActive}>{style.label}</Label>
              </StyleItem>
            );
          })}
        </ScrollContainer>
      </ScrollWrapper>
      {showRightArrow && (
        <ArrowButton
          $dir="right"
          onClick={() => handleScroll('right')}
          aria-label={tq`Scroll styles right`}
        >
          <ChevronRight
            aria-hidden
            title={undefined}
            size={20}
          />
        </ArrowButton>
      )}
    </Section>
  );
};
